<template>
  <div
    class="product-gallery grid grid--col-l7"
    :class="`product-gallery--${responsiveLayout}`"
  >
    <div
      v-if="showThumbnails"
      class="product-gallery__thumbnails-container col l1"
    >
      <product-gallery-carousel
        class="product-gallery__thumbnails-carousel"
        :current-index="currentIndex"
        :current-namespace="currentNamespace"
        :config="thumbnailsConfig"
        :layout="responsiveLayout"
        :loading="loading"
        :media="media"
        namespace="gallery"
        :section="section"
        :product="product"
        sizes="102px"
        widths="102, 204, 306"
        type="thumbnails"
        @update-current-index="handleUpdateCurrentIndex"
      />
    </div>

    <div class="col xs-span l6">
      <div class="product-gallery__main-container">
        <product-gallery-carousel
          class="product-gallery__main-carousel"
          :current-index="currentIndex"
          :current-namespace="currentNamespace"
          :config="mainConfig"
          :layout="responsiveLayout"
          :loading="loading"
          :media="media"
          namespace="gallery"
          :product="product"
          :sizes="sizes"
          widths="360, 720"
          type="main"
          :zoom="zoom"
          @update-current-index="handleUpdateCurrentIndex"
        />
        <inventory-progress-bar
          v-if="showInventoryProgress"
          :variant="variant"
        />
        <social-proof
          v-else-if="
            showSocialProof &&
            showInventoryProgress === false &&
            product.socialProof.text &&
            product.socialProof.images.length
          "
          :product="product"
        />
        <slot name="countdown" />
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-env es2020, browser */
/**
 * Vue: Product gallery (product-gallery)
 * -----------------------------------------------------------------------------
 * Wrapper for product gallery carousel.
 *
 * @param {Number} currentIndex - Current carousel index.
 * @param {String} currentNamespace - Current carousel namespace.
 * @param {String} layout - Product gallery layout, either `horizontal`,
 * `vertical`, `tabs`, or `grid`, supports a combination in the format
 * [mobile layout]-[desktop layout].
 * @param {Boolean} [loading] - Loading state.
 * @param {Object} product - Local product object.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 * @param {Boolean} [zoom] - Enable zoom functionality.
 * @param {Object} section - Main product section settings.
 *
 * @emits updateCurrentIndex - Emit update current carousel index event.
 * @emits updateCurrentNamespace - Emit update current carousel namespace event.
 *
 */
// eslint-disable-next-line import/named
import { mapState } from 'pinia';
import useIndexStore from '~stores/index';

import ProductGalleryCarousel from '~async/product-gallery-carousel/product-gallery-carousel';
import InventoryProgressBar from '~async/inventory-progress-bar/inventory-progress-bar';
import SocialProof from '~async/social-proof/social-proof';

import { values as breakpoints } from '~/config/breakpoints';

export default {
  name: 'ProductGallery',

  components: {
    ProductGalleryCarousel,
    InventoryProgressBar,
    SocialProof,
  },

  props: {
    currentIndex: {
      type: Number,
      default: 0,
      required: true,
    },
    currentNamespace: {
      type: String,
      default: 'gallery',
      validator: (value) => {
        return ['gallery', 'modal'].includes(value);
      },
      required: true,
    },
    layout: {
      type: String,
      default: 'horizontal',
      validator: (value) => {
        return value
          .split('-')
          .every((part) =>
            ['grid', 'horizontal', 'tabs', 'vertical'].includes(part),
          );
      },
    },
    loading: {
      type: Boolean,
      default: false,
    },
    section: {
      type: Object,
      default: () => ({
        id: '',
        showArrowsMobile: false,
        showGalleryVideo: false,
      }),
      required: true,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    variant: {
      type: [Boolean, Object],
      default: () => ({}),
      required: true,
    },
    zoom: {
      type: Boolean,
      default: true,
    },
    showSocialProof: {
      type: Boolean,
      default: false,
    },
  },

  emits: {
    'update-current-index': (payload) => typeof payload === 'number',
    'update-current-namespace': (payload) =>
      ['gallery', 'modal'].includes(payload),
  },

  data() {
    return {
      thumbnailsConfig: {
        arrows: false,
        direction: 'ttb',
        drag: false,
        height: '100%',
        gap: 'var(--spacing-l)',
        pagination: false,
        isNavigation: true,
        paginationDirection: 'ltr',
        keyboard: false,
      },
    };
  },

  computed: {
    /**
     * Map Pinia state.
     */
    ...mapState(useIndexStore, {
      isMobile: (store) => store.screen.mobile,
    }),

    /**
     * Compute main config.
     * - Splide inits desktop with wrong settings unless isMobile is used.
     * @returns {Object}
     */
    mainConfig() {
      return {
        arrows: this.isMobile && this.section.showArrowsMobile,
        pagination: false,
        height: '100%',
        perPage: this.media.length === 1 ? 1 : 1.5,
        gap: 'var(--spacing-xs)',
        focus: 'center',
        type: 'slide',
        keyboard: false,
        breakpoints: {
          [breakpoints.l]: {
            arrows: false,
            perPage: 1,
          },
        },
      };
    },

    localizedVideos() {
      return this.product?.videos
        ? this.product.videos.filter((video) => video.alt !== 'do-not-show')
        : [];
    },

    /**
     * Compute media array.
     * - Use product and variant metafield images if they exist.
     * @returns {Array}
     */
    media() {
      let media = [];

      const kameleoonID = cnvs.abtest?.experimentID;
      const kameleoonVariantID = cnvs.abtest?.variantID;

      const kameleoonExperiment = localStorage
        .getItem(`kameleoonExperiment-${kameleoonID}`)
        ?.includes(`${kameleoonVariantID}`);

      if (
        kameleoonExperiment &&
        (this.product?.abTestMedia?.length ||
          (this.variant?.abTestImage &&
            Object.keys(this.variant?.abTestImage)?.length))
      ) {
        if (this.product?.abTestMedia?.length) {
          media = this.product.abTestMedia;
        }
        if (
          this.variant?.abTestImage &&
          Object.keys(this.variant?.abTestImage)?.length
        ) {
          media = [this.variant.abTestImage, ...media];
        } else if (this.variant?.media?.length) {
          media = [...this.variant.media, ...media];
        } else if (
          this.variant?.featuredImage &&
          Object.keys(this.variant?.featuredImage)?.length
        ) {
          media = [this.variant.featuredImage, ...media];
        }
      } else {
        if (this.product?.media?.length) {
          media = this.product.media;
        }

        if (this.variant?.media?.length) {
          media = [...this.variant.media, ...media];
        } else if (
          this.variant?.localizedImage &&
          Object.keys(this.variant?.localizedImage)?.length
        ) {
          media = [this.variant.localizedImage, ...media];
        } else if (
          this.variant?.featuredImage &&
          Object.keys(this.variant?.featuredImage)?.length
        ) {
          media = [this.variant.featuredImage, ...media];
        }

        if (this.localizedVideos.length && this.section.showGalleryVideo) {
          media = [...media, ...this.localizedVideos];
        }
      }

      /**
       * Update with index for thumbnail click emit validation.
       */
      return media.map((item, index) => {
        return {
          ...item,
          index,
        };
      });
    },

    /**
     * Compute ZoomMedia array.
     * - Use existing Media, remove videos.
     * @returns {Array}
     */
    zoomMedia() {
      const galleryMedia = this.media;
      const zoomMedia = galleryMedia.filter(
        (item) => item.mediaType !== 'video',
      );

      /**
       * Update with index for thumbnail click emit validation.
       */
      return zoomMedia.map((item, index) => {
        return {
          ...item,
          index,
        };
      });
    },

    /**
     * Compute responsive layout based on mobile.
     * @returns {String}
     */
    responsiveLayout() {
      if (!this.layout.includes('-')) {
        return this.layout;
      }

      return this.isMobile
        ? this.layout.split('-')[0]
        : this.layout.split('-')[1];
    },

    /**
     * Compute whether to show thumbnails carousel.
     * @returns {Boolean}
     */
    showThumbnails() {
      return this.responsiveLayout === 'vertical';
    },

    /**
     * Compute responsive image sizes.
     * @returns {String}
     */
    sizes() {
      return `(min-width: ${breakpoints.l}px) 50vw, 100vw`;
    },

    /**
     * Returns true/false if inventory progress should show, or null if data not yet loaded.
     * Returning null prevents social-proof from showing prematurely (CLS fix).
     */
    showInventoryProgress() {
      // Guard against falsy/non-object variant or unloaded initialInventory
      if (
        !this.variant ||
        typeof this.variant !== 'object' ||
        typeof this.variant.initialInventory === 'undefined'
      ) {
        return null;
      }

      if (
        this.variant.initialInventory &&
        this.variant.initialInventory > 0 &&
        this.variant.availableState === 'available'
      ) {
        const remainingPercent = Math.round(
          (this.variant.inventory / this.variant.initialInventory) * 100,
        );
        return remainingPercent <= 75;
      }

      return false;
    },
  },

  watch: {
    /**
     * Watch variant to show matching image if it exists.
     */
    variant() {
      if (!this.variant) {
        return;
      }
      /**
       * Match media (either media or images array) with associated variant.
       */
      const matchedImageIndex = this.media.findIndex((media) => {
        return media.variant === this.variant.id;
      });

      if (matchedImageIndex < 0) {
        return;
      }

      this.$emit('update-current-index', matchedImageIndex);
    },
  },

  methods: {
    /**
     * Emit update current carousel index event.
     * @param {Number} newIndex - New media index.
     */
    handleUpdateCurrentIndex(newIndex) {
      this.$emit('update-current-index', newIndex);
    },

    /**
     * Emit update current carousel namespace event.
     * @param {Number} newNamespace - New active namespace.
     */
    handleUpdateCurrentNamespace(newNamespace) {
      this.$emit('update-current-namespace', newNamespace);
    },
  },
};
</script>

<style lang="scss">
@import '@/components/splide';
@import './product-gallery';
@import './product-gallery-tabs';
// @import './product-gallery-horizontal';
@import './product-gallery-vertical';
</style>
